import {
    LineBasicMaterial,
    Matrix4,
    MeshBasicMaterial,
    Quaternion,
    SphereGeometry,
    SpriteMaterial,
    Vector2,
} from 'three';
import { landmarkToImagePoint } from '@/views/3d-planner/hipSpine/hipSpineCoordinates';
import { markRaw, type Raw } from 'vue';
import { vector3 } from '@/domain/geometry/vector3';
import { meshNode } from '@/lib/threeScene/nodes/mesh';
import { lineSegmentsNode } from '@/lib/threeScene/nodes/lineSegments';
import { groupNode } from '@/lib/threeScene/nodes/group';
import { spriteNode, createLabelSpriteMaterial } from '@/lib/threeScene/nodes/sprite';
import type { StandardNode } from '@/lib/threeScene/updateNode';
import {
    HIP_SPINE_POSTURE_ANNOTATIONS,
    type HipSpineLandmarkGroup,
} from './hipSpineAnnotationDefs';
import type { HipSpineLandmarkApiObject } from '@/api/hip-spine-study/hipSpineLandmark';
import type { AnnotationGroup, HipSpineAnnotationCategory } from './types';
import type { XRayViewId } from '@/api/xrays';
import { resolveLabelOverlaps, type LabelPlacement } from './labelOverlapResolver';
import { buildFemurHeadCircleGroup } from './scene/femurHeadCircle';
import { HipSpineLandmarkName } from '@/domain/anatomy/hipSpineLandmarks';
import { connectingLineGeometry } from '@/views/3d-planner/hipSpine/scene/landmarkSceneSync';
import { DEFAULT_FEMUR_HEAD_RADIUS_FALLBACK } from '@/views/3d-planner/hipSpine/xrayEdit/editState';

type PendingAnnotationLabel = LabelPlacement & {
    label: string;
    groupIndex: number;
    groupId: string;
    material: Raw<SpriteMaterial>;
};

/** A pending label before its owning group's index is known. */
type PendingLabelDraft = Omit<PendingAnnotationLabel, 'groupIndex'>;

/** Build context shared, unchanged, across every subgroup of a posture. */
type BuildContext = {
    landmarkByName: Map<string, HipSpineLandmarkApiObject>;
    width: number;
    height: number;
    /**
     * Layout radius in image pixels: sizes and offsets the labels, and seeds each
     * marker's scale. NOT the rendered marker size — `scene/landmarkMarkerScale.ts`
     * re-derives that from the camera on every zoom.
     */
    sphereRadius: number;
    /** Shared unit sphere; each marker's world radius is its transform scale. */
    unitSphereGeometry: SphereGeometry;
};

/** Per-colour visual resources for one annotation section. */
type SubGroupStyle = {
    category: HipSpineAnnotationCategory;
    colour: string;
    sphereMaterial: MeshBasicMaterial;
    lineMaterial: LineBasicMaterial;
};

/** Geometry/material caches keyed by colour, shared across all sections. */
type StyleCaches = {
    sphere: Map<string, MeshBasicMaterial>;
    line: Map<string, LineBasicMaterial>;
};

const SPHERE_SEGMENTS = 32;

/**
 * Build annotation groups for a posture from landmarks and image size.
 * Returns plain AnnotationGroup[] without touching the scene.
 *
 * Geometry and materials are shared across landmarks of the same colour
 * to reduce GPU memory and draw-call overhead.
 */
export function buildAnnotationGroups(
    posture: XRayViewId,
    landmarks: HipSpineLandmarkApiObject[],
    imageSize: { width: number; height: number },
    femurRadii?: { 1?: number; 2?: number },
): AnnotationGroup[] {
    const { width, height } = imageSize;
    const sphereRadius = Math.max(width, height) * 0.008;
    const ctx: BuildContext = {
        landmarkByName: new Map(landmarks.map((lm) => [lm.name, lm])),
        width,
        height,
        sphereRadius,
        unitSphereGeometry: markRaw(new SphereGeometry(1, SPHERE_SEGMENTS, SPHERE_SEGMENTS)),
    };
    const annotations = HIP_SPINE_POSTURE_ANNOTATIONS[posture] ?? [];
    const caches: StyleCaches = { sphere: new Map(), line: new Map() };
    const groups: AnnotationGroup[] = [];
    const pendingLabels: PendingAnnotationLabel[] = [];

    for (const annotation of annotations) {
        const style = resolveStyle(annotation, caches);
        for (const subGroup of annotation.subGroups) {
            const built = buildSubGroupNodes(subGroup, style, ctx);
            if (!built) continue;
            groups.push(built.group);
            if (built.pendingLabel) {
                pendingLabels.push({ ...built.pendingLabel, groupIndex: groups.length - 1 });
            }
        }
    }

    attachFemurHeadCircles(groups, ctx, femurRadii);
    createAnnotationLabelSprites(groups, pendingLabels, sphereRadius);

    return groups;
}

/** Resolve (and memoise per colour) the shared materials for one section. */
function resolveStyle(
    annotation: { category: HipSpineAnnotationCategory; colour: string },
    caches: StyleCaches,
): SubGroupStyle {
    const { category, colour } = annotation;
    let sphereMaterial = caches.sphere.get(colour);
    if (!sphereMaterial) {
        sphereMaterial = markRaw(new MeshBasicMaterial({ color: colour }));
        caches.sphere.set(colour, sphereMaterial);
    }
    let lineMaterial = caches.line.get(colour);
    if (!lineMaterial) {
        lineMaterial = markRaw(new LineBasicMaterial({ color: colour, depthTest: false }));
        caches.line.set(colour, lineMaterial);
    }
    return { category, colour, sphereMaterial, lineMaterial };
}

/**
 * Build the scene group for one annotation subgroup (landmark dots + connecting
 * line) and prepare its label. Returns null when none of the subgroup's
 * landmarks are present for this posture.
 */
function buildSubGroupNodes(
    subGroup: HipSpineLandmarkGroup,
    style: SubGroupStyle,
    ctx: BuildContext,
): { group: AnnotationGroup; pendingLabel: PendingLabelDraft | null } | null {
    const { landmarkByName, width, height, sphereRadius, unitSphereGeometry } = ctx;
    const presentLandmarks = subGroup.landmarks
        .map((def) => landmarkByName.get(def.name))
        .filter((lm): lm is HipSpineLandmarkApiObject => lm !== undefined);

    if (presentLandmarks.length === 0) return null;

    const groupId = `group-${subGroup.label}`;
    const children: StandardNode[] = presentLandmarks.map((lm, i) => {
        const p = landmarkToImagePoint(lm, { width, height });
        return meshNode(`landmark-${subGroup.label}-${i}`, {
            parent: groupId,
            name: lm.name,
            geometry: unitSphereGeometry,
            material: style.sphereMaterial,
            transform: markerTransform(p, sphereRadius),
        });
    });

    if (presentLandmarks.length >= 2) {
        children.push(buildSubGroupLine(subGroup.label, presentLandmarks, style.lineMaterial, ctx));
    }

    const group: AnnotationGroup = {
        label: subGroup.label,
        name: subGroup.name ?? subGroup.label,
        category: style.category,
        colour: style.colour,
        sceneGroup: groupNode(groupId),
        children,
    };

    return {
        group,
        pendingLabel: prepareSubGroupLabel(subGroup.label, presentLandmarks, style, ctx),
    };
}

/** Places the shared unit sphere at `centre`, scaled to `radius`. */
function markerTransform(centre: { x: number; y: number }, radius: number): Matrix4 {
    return new Matrix4().compose(
        vector3(centre.x, centre.y, 0),
        new Quaternion(),
        vector3(radius, radius, radius),
    );
}

/** Build the poly-line connecting a subgroup's landmarks in order. */
function buildSubGroupLine(
    label: string,
    presentLandmarks: HipSpineLandmarkApiObject[],
    lineMaterial: LineBasicMaterial,
    ctx: BuildContext,
): StandardNode {
    return lineSegmentsNode(`line-${label}`, {
        parent: `group-${label}`,
        name: `line-${label}`,
        geometry: markRaw(
            connectingLineGeometry(presentLandmarks, { width: ctx.width, height: ctx.height }),
        ),
        material: lineMaterial,
    });
}

/**
 * Prepare a subgroup's label placement (sprite created later, after overlap
 * resolution). Returns null when the label material can't be built.
 */
function prepareSubGroupLabel(
    label: string,
    presentLandmarks: HipSpineLandmarkApiObject[],
    style: SubGroupStyle,
    ctx: BuildContext,
): PendingLabelDraft | null {
    const labelResult = createLabelSpriteMaterial(label, style.colour);
    if (!labelResult) return null;

    const { width, height, sphereRadius } = ctx;
    const labelHeight = sphereRadius * 5;
    const labelWidth = labelHeight * labelResult.aspectRatio;
    const rightmostLm = presentLandmarks.reduce((a, b) => (a.current_x >= b.current_x ? a : b));
    const base = landmarkToImagePoint(rightmostLm, { width, height });
    return {
        label,
        groupId: `group-${label}`,
        position: new Vector2(base.x + sphereRadius * 2 + labelWidth / 2, base.y),
        halfWidth: labelWidth / 2,
        halfHeight: labelHeight / 2,
        material: markRaw(labelResult.material),
    };
}

/**
 * Attach femur-head dotted circles to the proximal-femur groups (PF1/PF2).
 * Skips a head when its HJC landmark or femoral group is absent.
 */
function attachFemurHeadCircles(
    groups: AnnotationGroup[],
    ctx: BuildContext,
    femurRadii?: { 1?: number; 2?: number },
): void {
    const { landmarkByName, width, height } = ctx;
    for (const head of [1, 2] as const) {
        const hjcName = head === 1 ? HipSpineLandmarkName.HJC1 : HipSpineLandmarkName.HJC2;
        const hjc = landmarkByName.get(hjcName);
        if (!hjc) continue;
        const femurGroup = groups.find((g) => g.label === `PF${head}`);
        if (!femurGroup) continue;
        const circle = buildFemurHeadCircleGroup({
            head,
            parentId: femurGroup.sceneGroup.id,
            center: landmarkToImagePoint(hjc, { width, height }),
            // Radii are width-normalized fractions (the stored unit); scale to pixels here.
            radius: (femurRadii?.[head] ?? DEFAULT_FEMUR_HEAD_RADIUS_FALLBACK) * width,
            colour: femurGroup.colour,
        });
        femurGroup.children.push(circle.sceneGroup, ...circle.children);
    }
}

/** Resolve label overlaps across all groups, then create the label sprites. */
function createAnnotationLabelSprites(
    groups: AnnotationGroup[],
    pendingLabels: PendingAnnotationLabel[],
    sphereRadius: number,
): void {
    resolveLabelOverlaps(pendingLabels, sphereRadius);
    for (const pending of pendingLabels) {
        const labelWidth = pending.halfWidth * 2;
        const labelHeight = pending.halfHeight * 2;
        groups[pending.groupIndex].children.push(
            spriteNode(`label-${pending.label}`, {
                parent: pending.groupId,
                material: pending.material,
                transform: new Matrix4().compose(
                    vector3(pending.position.x, pending.position.y, 0),
                    new Quaternion(),
                    vector3(labelWidth, labelHeight, 1),
                ),
            }),
        );
    }
}
